import { useEffect, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  Badge,
  Button,
  Card,
  DataTable,
  Field,
  PageHeader,
  Select,
  Spinner,
} from "@empite/ui-components";
import type { BadgeTone } from "@empite/ui-components";
import type { Package, PackageVersion } from "../admin-client";
import { useApplications, usePackages } from "../admin-hooks";
import { ErrorAlert, RequireOperator, can } from "./guards";
import { when } from "./common";

/** The permissions the Admin API guards this surface with; repeated here
 *  only to decide what to draw. The routes re-check them. */
const READ = "packages:read";
const WRITE = "packages:write";

/**
 * One page of packages.
 *
 * A catalogue, not a search: an application has a handful of plans and the
 * whole suite has tens, so this limit exists to bound a runaway rather than
 * to paginate anything an operator will actually hit.
 */
const PAGE = 100;

const STATUSES = ["draft", "active", "archived"];

export interface PackagesSearch {
  application_id?: string;
  status?: string;
}

/**
 * Packages — the subscription offers, per application.
 *
 * Grouped by application rather than listed flat, because a package's price
 * is only comparable to the other packages of the SAME application: credit
 * bought here is held per application and is not transferable, so "the $20
 * plan" is a different product in App A than in App B. One table across both
 * would invite the comparison the product does not support.
 */
export function Packages({ search }: { search: PackagesSearch }) {
  return (
    <RequireOperator>
      {(session) =>
        can(session.operator, READ) ? (
          <PackageCatalogue search={search} writable={can(session.operator, WRITE)} />
        ) : (
          <NotPermitted />
        )
      }
    </RequireOperator>
  );
}

/**
 * What an operator without the permission a package screen needs sees.
 *
 * Named rather than blank: an empty screen reads as a page that failed, and
 * the operator raises a bug instead of asking for the grant they are missing.
 * This is a courtesy — the Admin API refuses the route itself and writes the
 * refusal to the audit trail.
 *
 * Exported because every screen in this section needs the same refusal, and
 * two of them worded differently is how an operator concludes one of the two
 * is broken.
 */
export function NotPermitted({
  title = "Packages",
  permission = READ,
}: {
  title?: string;
  permission?: string;
}) {
  return (
    <>
      <PageHeader title={title} />
      <Card>
        <p>
          Your roles do not include <code>{permission}</code>, so this is not something you can do
          from this account.
        </p>
      </Card>
    </>
  );
}

function PackageCatalogue({
  search,
  writable,
}: {
  search: PackagesSearch;
  writable: boolean;
}) {
  const navigate = useNavigate();
  const applications = useApplications();
  const [applicationID, setApplicationID] = useState(search.application_id ?? "");
  const [status, setStatus] = useState(search.status ?? "");

  // The URL is the source of truth, so a back or forward navigation has to
  // pull the controls along with it.
  useEffect(() => {
    setApplicationID(search.application_id ?? "");
    setStatus(search.status ?? "");
  }, [search.application_id, search.status]);

  const packages = usePackages({
    application_id: search.application_id,
    status: search.status,
    limit: PAGE,
  });
  const groups = groupByApplication(packages.data?.packages ?? []);

  return (
    <>
      <PageHeader
        title="Packages"
        description="Subscription plans, per application. Each one grants credit into the subscription bucket every time an invoice is paid, and that credit is cleared when the period ends."
        actions={
          writable ? (
            // Carries the application the operator is looking at, so the form
            // opens pointed at the same one. It is only ever a prefill — the
            // form still asks, and the server still decides.
            <Link
              className="esm-button esm-button--primary"
              to="/packages/new"
              search={{ application_id: search.application_id }}
            >
              Define a package
            </Link>
          ) : null
        }
      />
      <Card>
        <form
          className="esm-filters"
          onSubmit={(event) => {
            event.preventDefault();
            void navigate({
              to: "/packages",
              search: {
                application_id: applicationID || undefined,
                status: status || undefined,
              },
            });
          }}
        >
          <Field label="Application" htmlFor="package-application">
            <Select
              id="package-application"
              value={applicationID}
              onChange={(event) => setApplicationID(event.target.value)}
            >
              <option value="">Every application</option>
              {(applications.data?.applications ?? []).map((application) => (
                <option key={application.id} value={application.id}>
                  {application.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Status" htmlFor="package-status">
            <Select
              id="package-status"
              value={status}
              onChange={(event) => setStatus(event.target.value)}
            >
              <option value="">Any status</option>
              {STATUSES.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Select>
          </Field>
          <Button type="submit" busy={packages.isFetching}>
            Apply
          </Button>
        </form>
      </Card>
      <ErrorAlert error={packages.error} title="Could not load the packages" />
      {packages.isPending ? (
        <Card>
          <Spinner label="Loading packages" />
        </Card>
      ) : groups.length === 0 ? (
        <Card title="Packages">
          <p className="esm-table__empty">
            {search.application_id || search.status
              ? "No package matches that filter."
              : "No application has a subscription package yet, so nobody can subscribe to anything."}
          </p>
        </Card>
      ) : (
        groups.map((group) => (
          <ApplicationPackages key={group.applicationID} group={group} />
        ))
      )}
    </>
  );
}

interface ApplicationGroup {
  applicationID: string;
  applicationName: string;
  applicationSlug: string;
  packages: Package[];
}

/**
 * Split the list into one group per application, keeping the server's order.
 *
 * The Admin API already sorts by application name and then by each
 * application's own sort order, so first-appearance order IS the intended
 * order — re-sorting here would silently disagree with the order a customer
 * sees the same plans in.
 */
function groupByApplication(packages: Package[]): ApplicationGroup[] {
  const groups: ApplicationGroup[] = [];
  const byID = new Map<string, ApplicationGroup>();
  for (const pkg of packages) {
    let group = byID.get(pkg.application_id);
    if (!group) {
      group = {
        applicationID: pkg.application_id,
        applicationName: pkg.application_name,
        applicationSlug: pkg.application_slug,
        packages: [],
      };
      byID.set(pkg.application_id, group);
      groups.push(group);
    }
    group.packages.push(pkg);
  }
  return groups;
}

function ApplicationPackages({ group }: { group: ApplicationGroup }) {
  const live = group.packages.reduce((total, pkg) => total + pkg.subscriber_count, 0);

  return (
    <Card
      title={group.applicationName}
      actions={
        <span className="esm-muted-text">
          <code>{group.applicationSlug}</code> · {live.toLocaleString()}{" "}
          {live === 1 ? "subscriber" : "subscribers"}
        </span>
      }
    >
      <DataTable<Package>
        rows={group.packages}
        rowKey={(row) => row.id}
        empty="This application has no package."
        columns={[
          {
            key: "package",
            header: "Package",
            render: (row) => (
              <>
                <div>
                  <Link to="/packages/$packageId" params={{ packageId: row.id }}>
                    {row.name}
                  </Link>
                </div>
                <code className="esm-muted-text">{row.slug}</code>
              </>
            ),
          },
          {
            key: "price",
            header: "Price",
            align: "end",
            // Printed exactly as the wire sent it. These are decimal strings
            // the server formatted from integer micros; parsing one into a
            // Number to format it is how a plan starts being advertised at a
            // price nobody set.
            render: (row) => <PriceCell version={row.current_version} />,
          },
          {
            key: "granted",
            header: "Credit granted",
            align: "end",
            render: (row) => <MoneyCell version={row.current_version} field="granted_credit" />,
          },
          {
            key: "bonus",
            header: "Extra over top-up",
            align: "end",
            // The point of the whole product, as one number: what the
            // subscriber gets that the same money at the top-up counter would
            // not. Sent by the server, never subtracted here.
            render: (row) => <MoneyCell version={row.current_version} field="bonus" />,
          },
          {
            key: "status",
            header: "Status",
            render: (row) => (
              <div className="esm-badge-row">
                <Badge tone={statusTone(row.status)}>{row.status}</Badge>
                {/* A package with no version in force cannot be subscribed
                    to, whatever its status says. That is the sentence an
                    operator needs, and it is not derivable from the status
                    column beside it. */}
                {row.current_version ? null : <Badge tone="danger">no price</Badge>}
              </div>
            ),
          },
          {
            key: "subscribers",
            header: "Subscribers",
            align: "end",
            // Live subscriptions only, counted at read time. It is also what
            // decides whether this package can be withdrawn, which is why it
            // is a column rather than something on the detail page.
            render: (row) => row.subscriber_count.toLocaleString(),
          },
          {
            key: "billing",
            header: "Billed",
            render: (row) => <IntervalCell version={row.current_version} />,
          },
          { key: "updated", header: "Updated", render: (row) => when(row.updated_at) },
        ]}
      />
    </Card>
  );
}

/** Archived is not danger: a withdrawn package is a decision somebody made,
 *  not a fault, and colouring it red next to a genuinely broken row teaches
 *  an operator to ignore red. Draft is neutral for the same reason. */
function statusTone(status: string): BadgeTone {
  return status === "active" ? "success" : "neutral";
}

/** A missing version is stated, never blank — an empty money cell reads as a
 *  free plan. */
function NoVersion() {
  return <span className="esm-muted-text">no version in force</span>;
}

function PriceCell({ version }: { version: PackageVersion | null }) {
  if (!version) {
    return <NoVersion />;
  }
  return (
    <span className="esm-amount">
      {version.price} <span className="esm-muted-text">{version.currency}</span>
    </span>
  );
}

function MoneyCell({
  version,
  field,
}: {
  version: PackageVersion | null;
  field: "granted_credit" | "bonus";
}) {
  if (!version) {
    return <NoVersion />;
  }
  return (
    <span className="esm-amount">
      {version[field]} <span className="esm-muted-text">{version.currency}</span>
    </span>
  );
}

/** "every month", "every 3 months" — the interval as a customer experiences
 *  it, rather than the two fields the wire carries it in. */
function IntervalCell({ version }: { version: PackageVersion | null }) {
  if (!version) {
    return <NoVersion />;
  }
  const count = version.interval_count;
  return (
    <span>
      every {count === 1 ? version.billing_interval : `${count} ${version.billing_interval}s`}
    </span>
  );
}
